import type { ApiClient } from '../../api/client';
import type { WorkflowPort } from '../../features/workflow/public';
import { heydayPath, heydayPersonPath } from '../../config/heydayRoutes';

export function createWorkflowHttpAdapter(client: ApiClient): WorkflowPort {
  return {
    workflowRequestPath: (id) => heydayPath('my-workflow-requests', new URLSearchParams({ request: id })),
    staffingPlanPath: (id) => heydayPath('staffing', new URLSearchParams({ planId: id })),
    employmentContractPath: (workerId, effectiveDate) => heydayPersonPath(workerId, new URLSearchParams({ action: 'contracts', ...(effectiveDate ? { asOf: effectiveDate } : {}) })),
    listWorkflowTemplates: (includeDraft) => client.listWorkflowTemplates(includeDraft),
    getWorkflowTemplate: (id) => client.getWorkflowTemplate(id),
    createWorkflowTemplate: (input, key) => client.createWorkflowTemplate(input, key),
    updateWorkflowTemplate: (id, input, key) => client.updateWorkflowTemplate(id, input, key),
    createWorkflowTemplateVersion: (id, input, key) => client.createWorkflowTemplateVersion(id, input, key),
    updateWorkflowTemplateVersion: (id, input, key) => client.updateWorkflowTemplateVersion(id, input, key),
    publishWorkflowTemplateVersion: (id, revision, key) => client.publishWorkflowTemplateVersion(id, revision, key),
    listWorkflowRequests: (filters) => client.listWorkflowRequests(filters),
    getWorkflowRequest: (id) => client.getWorkflowRequest(id),
    createWorkflowRequest: (input, key) => client.createWorkflowRequest(input, key),
    updateWorkflowRequestDraft: (id, input, key) => client.updateWorkflowRequestDraft(id, input, key),
    submitWorkflowRequest: (id, version, key) => client.submitWorkflowRequest(id, version, key),
    cancelWorkflowRequest: (id, comment, key) => client.cancelWorkflowRequest(id, comment, key),
    copyWorkflowRequest: (id, key) => client.copyWorkflowRequest(id, key),
    getTimeOffRequest: (id) => client.getTimeOffRequest(id),
    timeOffEvidence: {
      async list(id) { return (await client.requestTenant<{ data: { id: string; displayName: string; scanStatus: string }[] }>(`/api/absenceManagement/v5/{tenant}/timeOffRequests/${encodeURIComponent(id)}/evidence`)).data; },
      download: (id) => client.downloadDocument(id),
    },
    async cancelTimeOffRequest(id, comment, idempotencyKey) {
      await client.cancelTimeOffRequest(id, comment, idempotencyKey);
    },
    searchWorkflowRequestReferences: (search) => client.searchWorkflowRequestReferences(search),
    exportWorkflowRequests: (filters) => client.exportWorkflowRequests(filters),
    listWorkflowTasks: (filters) => client.listWorkflowTasks(filters),
    getWorkflowTask: (stepId) => client.getWorkflowTask(stepId),
    async requestWorkflowTaskChanges(stepId, comment, key) {
      await client.requestWorkflowTaskChanges(stepId, comment, key);
    },
    async approveWorkflowTask(stepId, comment, key) {
      await client.approveWorkflowTask(stepId, comment, key);
    },
    async rejectWorkflowTask(stepId, comment, key) {
      await client.rejectWorkflowTask(stepId, comment, key);
    },
    listWorkflowRequestActivities: (id, cursor, limit) => client.listWorkflowRequestActivities(id, cursor, limit),
    createWorkflowRequestComment: (id, body, key) => client.createWorkflowRequestComment(id, body, key),
    uploadWorkflowRequestAttachment: (requestId, fieldKey, file) => client.uploadWorkflowRequestAttachment(requestId, fieldKey, file),
    getWorkflowRequestAttachment: (requestId, fieldKey, fileId) => client.getWorkflowRequestAttachment(requestId, fieldKey, fileId),
    downloadWorkflowRequestAttachment: (requestId, fieldKey, fileId, inline) => client.downloadWorkflowRequestAttachment(requestId, fieldKey, fileId, inline),
    deleteWorkflowRequestAttachment: (requestId, fieldKey, fileId) => client.deleteWorkflowRequestAttachment(requestId, fieldKey, fileId),
  };
}
